/**
 * Milestone Share Card Component
 * Premium, social-share friendly cards for viral sharing
 * Designed for future Instagram, TikTok, Snapchat integration
 */

import React, { forwardRef } from 'react';
import {
  Trophy,
  Medal,
  Crown,
  Star,
  Flame,
  Award,
  PartyPopper,
  Users,
  Calendar,
  Sparkles,
  Shield,
  Zap,
  TrendingUp,
  CheckCircle,
  Target,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Icon mapping for badges and milestones
const ICONS = {
  trophy: Trophy,
  medal: Medal,
  crown: Crown,
  star: Star,
  flame: Flame,
  award: Award,
  party: PartyPopper,
  users: Users,
  calendar: Calendar,
  sparkles: Sparkles,
  shield: Shield,
  zap: Zap,
  trending: TrendingUp,
  check: CheckCircle,
  target: Target,
};

// Color themes for different milestone types
const THEMES = {
  gold: {
    gradient: 'from-amber-500 via-yellow-400 to-amber-600',
    accent: 'text-amber-400',
    bg: 'bg-amber-500/20',
    border: 'border-amber-500/30',
    glow: 'shadow-amber-500/30',
  },
  platinum: {
    gradient: 'from-slate-300 via-white to-slate-400',
    accent: 'text-slate-200',
    bg: 'bg-slate-400/20',
    border: 'border-slate-400/30',
    glow: 'shadow-slate-400/30',
  },
  violet: {
    gradient: 'from-violet-500 via-purple-500 to-violet-600',
    accent: 'text-violet-400',
    bg: 'bg-violet-500/20',
    border: 'border-violet-500/30',
    glow: 'shadow-violet-500/30',
  },
  cyan: {
    gradient: 'from-cyan-400 via-teal-400 to-cyan-500',
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    border: 'border-cyan-500/30',
    glow: 'shadow-cyan-500/30',
  },
  rose: {
    gradient: 'from-rose-500 via-pink-500 to-rose-600',
    accent: 'text-rose-400',
    bg: 'bg-rose-500/20',
    border: 'border-rose-500/30',
    glow: 'shadow-rose-500/30',
  },
  emerald: {
    gradient: 'from-emerald-500 via-green-400 to-emerald-600',
    accent: 'text-emerald-400',
    bg: 'bg-emerald-500/20',
    border: 'border-emerald-500/30',
    glow: 'shadow-emerald-500/30',
  },
  orange: {
    gradient: 'from-orange-500 via-amber-500 to-orange-600',
    accent: 'text-orange-400',
    bg: 'bg-orange-500/20',
    border: 'border-orange-500/30',
    glow: 'shadow-orange-500/30',
  },
};

// ==========================================
// BASE SHARE CARD (Ref-forwarded for export)
// ==========================================
export const ShareCard = forwardRef(({ 
  children, 
  theme = 'violet',
  className = '',
  size = 'default', // 'default' | 'story' | 'square'
}, ref) => {
  const themeStyles = THEMES[theme] || THEMES.violet;
  
  const sizeClasses = {
    default: 'w-[400px] h-[500px]',
    story: 'w-[360px] h-[640px]', // 9:16 for stories
    square: 'w-[400px] h-[400px]', // 1:1 for posts
  };
  
  return (
    <div
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-3xl',
        'bg-gradient-to-br from-[#0a0a14] via-[#12121e] to-[#0a0a14]',
        sizeClasses[size],
        className
      )}
      data-testid="share-card"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,80,200,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(80,200,200,0.1),transparent_50%)]" />
      </div>
      
      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      
      {/* Content */}
      <div className="relative h-full flex flex-col p-6">
        {children}
      </div>
      
      {/* Bottom branding */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] backdrop-blur-sm border border-white/[0.08]">
          <div className="w-5 h-5 rounded-md bg-cyan-500 flex items-center justify-center">
            <span className="text-xs font-bold text-white">C</span>
          </div>
          <span className="text-xs font-medium text-white/60">CamPassed</span>
        </div>
      </div>
    </div>
  );
});

ShareCard.displayName = 'ShareCard';

// ==========================================
// BADGE EARNED CARD
// ==========================================
export const BadgeEarnedCard = forwardRef(({
  badgeName,
  badgeDescription,
  badgeIcon = 'award',
  badgeTier = 'gold',
  earnedAt,
  userName,
  size = 'default',
}, ref) => {
  const Icon = ICONS[badgeIcon] || Award;
  const theme = THEMES[badgeTier] || THEMES.gold;
  
  return (
    <ShareCard ref={ref} theme={badgeTier} size={size}>
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08]">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">New Badge Unlocked</span>
        </div>
      </div>
      
      {/* Badge Icon */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className={cn(
          'w-32 h-32 rounded-3xl flex items-center justify-center mb-6',
          'bg-gradient-to-br',
          theme.gradient,
          'shadow-2xl',
          theme.glow
        )}>
          <Icon className="w-16 h-16 text-white drop-shadow-lg" />
        </div>
        
        <h2 className="text-3xl font-bold text-white text-center mb-2">
          {badgeName}
        </h2>
        
        <p className="text-white/60 text-center max-w-[280px] text-sm">
          {badgeDescription}
        </p>
      </div>
      
      {/* Footer */}
      <div className="mt-auto pt-6 text-center">
        {userName && (
          <p className="text-white/40 text-sm mb-2">Earned by</p>
        )}
        <p className={cn('text-lg font-semibold', theme.accent)}>
          {userName || 'Campus Legend'}
        </p>
        {earnedAt && (
          <p className="text-white/30 text-xs mt-1">
            {new Date(earnedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}
      </div>
    </ShareCard>
  );
});

BadgeEarnedCard.displayName = 'BadgeEarnedCard';

// ==========================================
// LEADERBOARD RANK CARD
// ==========================================
export const LeaderboardRankCard = forwardRef(({
  rank,
  totalUsers,
  score,
  leaderboardType = 'student', // 'student' | 'organization' | 'campus'
  period = 'All Time',
  userName,
  campusName,
  size = 'default',
}, ref) => {
  const isTop10 = rank <= 10;
  const isTop3 = rank <= 3;
  const percentile = totalUsers > 0 ? Math.round((1 - (rank - 1) / totalUsers) * 100) : 0;
  
  const theme = isTop3 ? 'gold' : isTop10 ? 'violet' : 'cyan';
  const themeStyles = THEMES[theme];
  
  const getRankIcon = () => {
    if (rank === 1) return Crown;
    if (rank === 2) return Medal;
    if (rank === 3) return Medal;
    return Trophy;
  };
  
  const RankIcon = getRankIcon();
  
  return (
    <ShareCard ref={ref} theme={theme} size={size}>
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08]">
          <TrendingUp className={cn('w-4 h-4', themeStyles.accent)} />
          <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">
            {isTop10 ? 'Top 10 on Campus!' : 'Leaderboard Rank'}
          </span>
        </div>
      </div>
      
      {/* Rank Display */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className={cn(
          'relative w-36 h-36 rounded-full flex items-center justify-center mb-6',
          'bg-gradient-to-br',
          themeStyles.gradient,
          'shadow-2xl',
          themeStyles.glow
        )}>
          {isTop3 && (
            <div className="absolute -top-2 -right-2">
              <RankIcon className="w-10 h-10 text-white drop-shadow-lg" />
            </div>
          )}
          <span className="text-6xl font-bold text-white font-mono">
            #{rank}
          </span>
        </div>
        
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          {isTop3 ? (rank === 1 ? '🏆 Champion!' : rank === 2 ? '🥈 Runner Up!' : '🥉 Third Place!') : 
           isTop10 ? '🔥 Top 10!' : `Top ${percentile}%`}
        </h2>
        
        <div className="flex items-center gap-4 text-white/60 text-sm">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4" />
            {score?.toLocaleString() || 0} points
          </span>
          <span>•</span>
          <span>{period}</span>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-auto pt-6 text-center">
        <p className={cn('text-xl font-semibold', themeStyles.accent)}>
          {userName || 'Campus Legend'}
        </p>
        {campusName && (
          <p className="text-white/40 text-sm mt-1">{campusName}</p>
        )}
      </div>
    </ShareCard>
  );
});

LeaderboardRankCard.displayName = 'LeaderboardRankCard';

// ==========================================
// EVENTS MILESTONE CARD
// ==========================================
export const EventsMilestoneCard = forwardRef(({
  eventsCount,
  milestone, // The milestone reached (e.g., 5, 10, 25, 50, 100)
  userName,
  totalCheckIns,
  size = 'default',
}, ref) => {
  const getMilestoneTheme = () => {
    if (milestone >= 100) return 'gold';
    if (milestone >= 50) return 'violet';
    if (milestone >= 25) return 'cyan';
    if (milestone >= 10) return 'emerald';
    return 'rose';
  };
  
  const getMilestoneEmoji = () => {
    if (milestone >= 100) return '👑';
    if (milestone >= 50) return '🔥';
    if (milestone >= 25) return '⭐';
    if (milestone >= 10) return '🎉';
    return '🎊';
  };
  
  const theme = getMilestoneTheme();
  const themeStyles = THEMES[theme];
  
  return (
    <ShareCard ref={ref} theme={theme} size={size}>
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08]">
          <PartyPopper className={cn('w-4 h-4', themeStyles.accent)} />
          <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">Milestone Reached!</span>
        </div>
      </div>
      
      {/* Milestone Display */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-6xl mb-4">{getMilestoneEmoji()}</div>
        
        <div className={cn(
          'text-7xl font-bold font-mono mb-4',
          'bg-gradient-to-r bg-clip-text text-transparent',
          themeStyles.gradient
        )}>
          {eventsCount}
        </div>
        
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          Events Attended
        </h2>
        
        {totalCheckIns && (
          <p className="text-white/50 text-sm flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {totalCheckIns} total check-ins
          </p>
        )}
      </div>
      
      {/* Footer */}
      <div className="mt-auto pt-6 text-center">
        <p className={cn('text-xl font-semibold', themeStyles.accent)}>
          {userName || 'Campus Explorer'}
        </p>
        <p className="text-white/40 text-sm mt-1">Keep the streak going! 🚀</p>
      </div>
    </ShareCard>
  );
});

EventsMilestoneCard.displayName = 'EventsMilestoneCard';

// ==========================================
// VIP STATUS CARD
// ==========================================
export const VIPStatusCard = forwardRef(({
  statusName = 'VIP',
  statusDescription,
  userName,
  benefits = [],
  size = 'default',
}, ref) => {
  return (
    <ShareCard ref={ref} theme="gold" size={size}>
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30">
          <Crown className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Exclusive Status</span>
        </div>
      </div>
      
      {/* VIP Display */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="relative mb-6">
          <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-600 flex items-center justify-center shadow-2xl shadow-amber-500/30">
            <Crown className="w-14 h-14 text-white drop-shadow-lg" />
          </div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 rounded-full">
            <span className="text-xs font-bold text-black uppercase">{statusName}</span>
          </div>
        </div>
        
        <h2 className="text-3xl font-bold text-white text-center mb-2">
          {statusName} Status Earned
        </h2>
        
        {statusDescription && (
          <p className="text-white/60 text-center max-w-[280px] text-sm mb-4">
            {statusDescription}
          </p>
        )}
        
        {benefits.length > 0 && (
          <div className="space-y-2 w-full max-w-[280px]">
            {benefits.slice(0, 3).map((benefit, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/70">
                <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="mt-auto pt-6 text-center">
        <p className="text-xl font-semibold text-amber-400">
          {userName || 'VIP Member'}
        </p>
      </div>
    </ShareCard>
  );
});

VIPStatusCard.displayName = 'VIPStatusCard';

// ==========================================
// PASSPORT MILESTONE CARD
// ==========================================
export const PassportMilestoneCard = forwardRef(({
  milestoneType, // 'badges' | 'stamps' | 'events' | 'streak'
  milestoneValue,
  milestoneName,
  userName,
  size = 'default',
}, ref) => {
  const getConfig = () => {
    switch (milestoneType) {
      case 'badges':
        return { icon: Award, theme: 'violet', label: 'Badges Earned', emoji: '🏅' };
      case 'stamps':
        return { icon: CheckCircle, theme: 'cyan', label: 'Passport Stamps', emoji: '📍' };
      case 'events':
        return { icon: Calendar, theme: 'emerald', label: 'Events Attended', emoji: '🎫' };
      case 'streak':
        return { icon: Flame, theme: 'orange', label: 'Day Streak', emoji: '🔥' };
      default:
        return { icon: Star, theme: 'gold', label: 'Achievement', emoji: '⭐' };
    }
  };
  
  const config = getConfig();
  const Icon = config.icon;
  const themeStyles = THEMES[config.theme];
  
  return (
    <ShareCard ref={ref} theme={config.theme} size={size}>
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08]">
          <Icon className={cn('w-4 h-4', themeStyles.accent)} />
          <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">Passport Milestone</span>
        </div>
      </div>
      
      {/* Milestone Display */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-6xl mb-4">{config.emoji}</div>
        
        <div className={cn(
          'text-7xl font-bold font-mono mb-2',
          'bg-gradient-to-r bg-clip-text text-transparent',
          themeStyles.gradient
        )}>
          {milestoneValue}
        </div>
        
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          {milestoneName || config.label}
        </h2>
        
        <p className="text-white/50 text-sm">
          Keep exploring campus life! 🎓
        </p>
      </div>
      
      {/* Footer */}
      <div className="mt-auto pt-6 text-center">
        <p className={cn('text-xl font-semibold', themeStyles.accent)}>
          {userName || 'Campus Explorer'}
        </p>
      </div>
    </ShareCard>
  );
});

PassportMilestoneCard.displayName = 'PassportMilestoneCard';

export default ShareCard;
